import type { Dispatch, ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { SHAPES, tableSchema, type TableFormValues } from "../editor/schema";
import type { EditorAction } from "../editor/state";
import type { DraftTable } from "../editor/types";

/**
 * The selected-table inspector (Implementation plan §4): ADR-0027 RHF + zod. Every pointer gesture has
 * a form equivalent here — position/rotation numeric fields, covers, shape, label, priority — so the
 * editor is fully keyboard/AT operable (the a11y path). FL-1 (`minCovers ≤ maxCovers`) is a schema
 * refinement, surfaced inline on `maxCovers`; the whole-draft Save gate re-checks it (validatePlan).
 * Each change dispatches an element-granular reducer action so the canvas updates live and undo works.
 */
export interface TableInspectorProps {
  table: DraftTable;
  zoneName: string;
  disabled: boolean;
  dispatch: Dispatch<EditorAction>;
}

export function TableInspector({ table, zoneName, disabled, dispatch }: TableInspectorProps) {
  const { t } = useTranslation();
  const values: TableFormValues = {
    label: table.label,
    shape: table.shape,
    x: table.x,
    y: table.y,
    rotationDeg: table.rotationDeg,
    minCovers: table.minCovers,
    maxCovers: table.maxCovers,
    priorityOrder: table.priorityOrder,
  };
  const form = useForm<TableFormValues>({
    resolver: zodResolver(tableSchema) as Resolver<TableFormValues>,
    values,
    mode: "onChange",
  });

  const err = (name: keyof TableFormValues): string | undefined => {
    const m = form.formState.errors[name]?.message;
    return m ? t(m) : undefined;
  };

  const id = table.id;

  return (
    <section
      aria-label={t("floorPlan.inspector.title", { label: table.label })}
      className={css({ display: "flex", flexDirection: "column", gap: "3" })}
    >
      <h3 className={css({ fontSize: "md", fontWeight: "semibold" })}>
        {t("floorPlan.inspector.table", { zone: zoneName })}
      </h3>

      <Field label={t("floorPlan.inspector.label")} error={err("label")}>
        <input
          className={inputStyle}
          disabled={disabled}
          value={table.label}
          {...form.register("label", {
            onChange: (e) => dispatch({ type: "SET_TABLE_LABEL", id, label: e.target.value }),
          })}
        />
      </Field>

      <Field label={t("floorPlan.inspector.shape")}>
        <select
          className={inputStyle}
          disabled={disabled}
          value={table.shape}
          {...form.register("shape", {
            onChange: (e) =>
              dispatch({
                type: "SET_TABLE_SHAPE",
                id,
                shape: e.target.value as TableFormValues["shape"],
              }),
          })}
        >
          {SHAPES.map((s) => (
            <option key={s} value={s}>
              {t(`floorPlan.shape.${s}`)}
            </option>
          ))}
        </select>
      </Field>

      <div className={css({ display: "flex", gap: "3" })}>
        <Field label={t("floorPlan.inspector.x")}>
          <input
            type="number"
            className={inputStyle}
            disabled={disabled}
            value={table.x}
            onChange={(e) =>
              dispatch({ type: "SET_TABLE_POSITION", id, x: num(e.target.value), y: table.y })
            }
          />
        </Field>
        <Field label={t("floorPlan.inspector.y")}>
          <input
            type="number"
            className={inputStyle}
            disabled={disabled}
            value={table.y}
            onChange={(e) =>
              dispatch({ type: "SET_TABLE_POSITION", id, x: table.x, y: num(e.target.value) })
            }
          />
        </Field>
        <Field label={t("floorPlan.inspector.rotation")}>
          <input
            type="number"
            className={inputStyle}
            min={0}
            max={359}
            disabled={disabled}
            value={table.rotationDeg}
            onChange={(e) =>
              dispatch({ type: "SET_TABLE_ROTATION", id, deg: num(e.target.value) || 0 })
            }
          />
        </Field>
      </div>

      <div className={css({ display: "flex", gap: "3", alignItems: "flex-start" })}>
        <Field label={t("floorPlan.inspector.minCovers")} error={err("minCovers")}>
          <input
            type="number"
            min={1}
            className={inputStyle}
            disabled={disabled}
            value={table.minCovers}
            {...form.register("minCovers", {
              valueAsNumber: true,
              onChange: (e) =>
                dispatch({
                  type: "SET_COVERS",
                  id,
                  minCovers: num(e.target.value),
                  maxCovers: table.maxCovers,
                }),
            })}
          />
        </Field>
        <Field label={t("floorPlan.inspector.maxCovers")} error={err("maxCovers")}>
          <input
            type="number"
            min={1}
            className={inputStyle}
            disabled={disabled}
            value={table.maxCovers}
            {...form.register("maxCovers", {
              valueAsNumber: true,
              onChange: (e) =>
                dispatch({
                  type: "SET_COVERS",
                  id,
                  minCovers: table.minCovers,
                  maxCovers: num(e.target.value),
                }),
            })}
          />
        </Field>
        <Field label={t("floorPlan.inspector.priority")}>
          <input
            type="number"
            min={0}
            className={inputStyle}
            disabled={disabled}
            value={table.priorityOrder}
            onChange={(e) =>
              dispatch({ type: "SET_PRIORITY", id, priorityOrder: num(e.target.value) || 0 })
            }
          />
        </Field>
      </div>

      <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => dispatch({ type: "ROTATE_TABLE", id, delta: -15 })}
          className={btnStyle}
        >
          {t("floorPlan.inspector.rotateLeft")}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => dispatch({ type: "ROTATE_TABLE", id, delta: 15 })}
          className={btnStyle}
        >
          {t("floorPlan.inspector.rotateRight")}
        </button>
        {table.retired ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => dispatch({ type: "RESTORE_TABLE", id })}
            className={btnStyle}
          >
            {t("floorPlan.inspector.restore")}
          </button>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => dispatch({ type: "RETIRE_TABLE", id })}
            className={css({
              px: "3",
              py: "1",
              rounded: "md",
              borderWidth: "1px",
              fontSize: "sm",
              color: "fg.error",
            })}
          >
            {table.isNew ? t("floorPlan.inspector.delete") : t("floorPlan.inspector.retire")}
          </button>
        )}
      </div>
    </section>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "1",
        flex: "1",
        fontSize: "sm",
      })}
    >
      <span>{label}</span>
      {children}
      {error ? <span className={css({ color: "fg.error", fontSize: "xs" })}>{error}</span> : null}
    </label>
  );
}

const inputStyle = css({ px: "2", py: "1", borderWidth: "1px", rounded: "md", w: "full" });
const btnStyle = css({ px: "3", py: "1", rounded: "md", borderWidth: "1px", fontSize: "sm" });

/** Parse a numeric input, mapping an empty string to NaN so zod flags it rather than coercing to 0. */
const num = (v: string) => (v.trim() === "" ? Number.NaN : Number(v));
